import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, combineLatest, map, zip } from "rxjs";
import { CurrentUserService } from "@gtmhub/users";
import { HomeWidgetSettings } from "@gtmhub/users/models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureModuleService } from "@webapp/feature-toggles/services/feature-module.service";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { UiDashboardWidget } from "@webapp/ui/dashboard/dashboard.models";
import { handleWidgetsChange } from "../components/home-dashboard/home-dashboard.utils";
import { KICKSTART_OKR_WIDGET, SUPPORTED_WIDGETS } from "../models/home-widgets.models";

@Injectable()
export class HomeWidgetService {
  private widgets: UiDashboardWidget[] = [...SUPPORTED_WIDGETS];
  private changes$ = new BehaviorSubject<void>(undefined);
  private availability$: Observable<Record<string, boolean>>;

  constructor(
    private featureModuleService: FeatureModuleService,
    private permissionsFacade: PermissionsFacade,
    private featureTogglesFacade: FeatureTogglesFacade,
    private currentUserService: CurrentUserService
  ) {}

  public getDashboardWidgets$(): Observable<UiDashboardWidget[]> {
    if (!this.availability$) {
      this.availability$ = zip([this.isCheckInsWidgetAvailable$(), this.isWhiteboardsWidgetAvailable$(), this.isTodosWidgetAvailable$()]).pipe(
        map(([isCheckInsWidgetAvailable, isWhiteboardsWidgetAvailable, isTodosWidgetAvailable]) => {
          const isViewOnly = this.currentUserService.userHasRole("view-only");
          const closed = isViewOnly ? this.getCloseWidgetIdsFromUserSettings() : [];
          return {
            "check-ins": isCheckInsWidgetAvailable,
            whiteboards: isWhiteboardsWidgetAvailable,
            "to-do": isTodosWidgetAvailable,
            "view-alignment": isViewOnly && !closed.includes("view-alignment"),
            "view-feed": isViewOnly && !closed.includes("view-feed"),
            "kickstart-okr": isViewOnly && !closed.includes("kickstart-okr"),
          };
        })
      );
    }

    return combineLatest([this.availability$, this.changes$]).pipe(
      map(([availability]) => {
        const widgets = this.widgets.filter((widget) => availability[widget.id] !== false);
        if (widgets.length === 1) {
          widgets.push(KICKSTART_OKR_WIDGET);
        }
        return widgets;
      })
    );
  }

  public handleWidgetsChange(changedWidgets: UiDashboardWidget[], equalityOptions: { checkForY: boolean } = { checkForY: false }): void {
    const widgets = handleWidgetsChange(this.widgets, changedWidgets, equalityOptions);

    if (this.widgets !== widgets) {
      this.widgets = widgets;
      this.changes$.next();
    }
  }

  public addWidget(widgetId: string): void {
    const widget = SUPPORTED_WIDGETS.find((w) => w.id === widgetId);
    if (!widget) {
      throw new Error(`Widget ${widgetId} does not exist`);
    }

    this.widgets.push(widget);
    this.changes$.next();
    this.unmarkWidgetAsClosedInUserSettings(widgetId);
  }

  public removeWidget(widgetId: string): void {
    const index = this.widgets.findIndex((w) => w.id === widgetId);
    if (index < 0) {
      throw new Error(`Widget ${widgetId} is not present`);
    }

    // update the last 'y' in case we want to add the widget again
    const widget = SUPPORTED_WIDGETS.find((w) => w.id === widgetId);
    widget.y = this.widgets[index].y;

    this.widgets.splice(index, 1);
    this.changes$.next();
    this.markWidgetAsClosedInUserSettings(widgetId);
  }

  public isFullWidth(widgetId: string): boolean {
    const widget = this.getWidgetById(widgetId);
    return widget.w === 2;
  }

  public canToggleFullWidth(widgetId: string): boolean {
    const widget = this.getWidgetById(widgetId);
    return !widget.noResize;
  }

  public canMoveUp(widgetId: string): boolean {
    const widget = this.getWidgetById(widgetId);
    const widgetsAbove = this.getWidgetsOnRowAbove(widget);
    return widgetsAbove.length > 0 && !widgetsAbove[0].noMove && !widgetsAbove[0].locked;
  }

  public canMoveDown(widgetId: string): boolean {
    const widget = this.getWidgetById(widgetId);
    const widgetsBelow = this.getWidgetsOnRowBelow(widget);
    return widgetsBelow.length > 0 && !widgetsBelow[0].noMove && !widgetsBelow[0].locked;
  }

  public canMoveLeft(widgetId: string): boolean {
    const widget = this.getWidgetById(widgetId);
    return widget.w === 2 ? false : widget.x > 0;
  }

  public canMoveRight(widgetId: string): boolean {
    const widget = this.getWidgetById(widgetId);
    return widget.w === 2 ? false : widget.x < 1;
  }

  public toggleFullWidth(widgetId: string): void {
    const widget = this.getWidgetById(widgetId);
    this.handleWidgetsChange([
      {
        ...widget,
        x: 0,
        w: widget.w === 1 ? 2 : 1,
      },
    ]);
  }

  public moveUp(widgetId: string): void {
    if (!this.canMoveUp(widgetId)) {
      throw new Error(`Cannot move widget ${widgetId} up`);
    }

    const widget = this.getWidgetById(widgetId);
    const widgetsAbove = this.getWidgetsOnRowAbove(widget);

    // If the widget we are moving up is full width
    if (widget.w === 2) {
      const changedWidgets = this.moveFullWidthWidgetUp(widget);
      this.handleWidgetsChange(changedWidgets, { checkForY: true });
      return;
    }

    // If above the widget we are moving there is a widget with the same size and position, just swap them
    const widgetAboveWithSameWidthAndPosition = widgetsAbove.find((w) => w.w === widget.w && w.x === widget.x);
    if (widgetAboveWithSameWidthAndPosition) {
      const changedWidgets = this.swapWidgetsVertically(widget, widgetAboveWithSameWidthAndPosition);
      this.handleWidgetsChange(changedWidgets, { checkForY: true });
      return;
    }

    // If there is free space for the widget to be placed above, place the widget there
    const widgetsTwoLevelsAbove = this.getWidgetsOnRowAbove(widgetsAbove[0]);
    if (widgetsTwoLevelsAbove.length === 1 && widgetsTwoLevelsAbove[0].w === 1 && widgetsTwoLevelsAbove[0].x !== widget.x) {
      const movedWidget = { ...widget };
      movedWidget.y = widgetsTwoLevelsAbove[0].y;

      // If there is not a widget on the same row as the widget before moving it up, move all widgets below it up to fill the emty space after widget has been moved
      const widgetOnSameRow = this.getWidgetOnSameRow(widget);
      let additionalMovedWidgets = [];
      if (!widgetOnSameRow) {
        additionalMovedWidgets = this.moveWidgetsBelowVerticallyBy(widget, -widget.h);
      }

      this.handleWidgetsChange([movedWidget, ...additionalMovedWidgets], { checkForY: true });
      return;
    }

    // Move the widget above and fix vertical position of all widgets below it
    const movedWidget = { ...widget };
    movedWidget.y = widgetsAbove[0].y;

    const additionalMovedWidgets: UiDashboardWidget[] = [];

    const widgetOnSameRowAfterMove = this.getWidgetOnSameRow(movedWidget);
    widgetOnSameRowAfterMove.y += movedWidget.h;
    additionalMovedWidgets.push(widgetOnSameRowAfterMove);

    const widgetOnSameRowBeforeMove = this.getWidgetOnSameRow(widget);
    if (widgetOnSameRowBeforeMove) {
      additionalMovedWidgets.push(...this.moveWidgetsBelowVerticallyBy(movedWidget, movedWidget.h));
    }

    this.handleWidgetsChange([movedWidget, ...additionalMovedWidgets], { checkForY: true });
  }

  public moveDown(widgetId: string): void {
    if (!this.canMoveDown(widgetId)) {
      throw new Error(`Cannot move widget ${widgetId} down`);
    }

    const widget = this.getWidgetById(widgetId);
    const widgetsBelow = this.getWidgetsOnRowBelow(widget);

    // If the widget we are moving up is full width
    if (widget.w === 2) {
      const changedWidgets = this.moveFullWidthWidgetDown(widget);
      this.handleWidgetsChange(changedWidgets, { checkForY: true });
      return;
    }

    // If below the widget we are moving there is a widget with the same size and position, just swap them
    const widgetBelowWithSameWidthAndPosition = widgetsBelow.find((w) => w.w === widget.w && w.x === widget.x);
    if (widgetBelowWithSameWidthAndPosition) {
      const changedWidgets = this.swapWidgetsVertically(widget, widgetBelowWithSameWidthAndPosition);
      this.handleWidgetsChange(changedWidgets, { checkForY: true });
      return;
    }

    // If there is free space for the widget to be placed below, place the widget there
    const widgetsTwoLevelsBelow = this.getWidgetsOnRowBelow(widgetsBelow[0]);
    if (widgetsTwoLevelsBelow.length === 1 && widgetsTwoLevelsBelow[0].w === 1 && widgetsTwoLevelsBelow[0].x !== widget.x) {
      const movedWidget = { ...widget };
      movedWidget.y = widgetsBelow[0].y + widgetsBelow[0].h;

      // If there is not a widget on the same row as the widget before moving it up, move all widgets below it up to fill the emty space after widget has been moved
      const widgetOnSameRow = this.getWidgetOnSameRow(widget);
      let additionalMovedWidgets = [];
      if (!widgetOnSameRow) {
        movedWidget.y -= widget.h;
        additionalMovedWidgets = this.moveWidgetsBelowVerticallyBy(widget, -widget.h);
      }

      this.handleWidgetsChange([movedWidget, ...additionalMovedWidgets], { checkForY: true });
      return;
    }

    const widgetOnSameRowBeforeMove = this.getWidgetOnSameRow(widget);
    if (!widgetOnSameRowBeforeMove) {
      const changedWidgets = this.swapWidgetsVertically(widget, widgetsBelow[0]);
      this.handleWidgetsChange(changedWidgets, { checkForY: true });
      return;
    }

    const movedWidget = { ...widget };
    movedWidget.y = widgetsBelow[0].y + widgetsBelow[0].h;

    const additionalMovedWidgets: UiDashboardWidget[] = [];

    const widgetOnSameRowAfterMove = this.getWidgetOnSameRow(movedWidget);
    if (widgetOnSameRowAfterMove) {
      widgetOnSameRowAfterMove.y += movedWidget.h;
      additionalMovedWidgets.push(widgetOnSameRowAfterMove);
    }

    additionalMovedWidgets.push(...this.moveWidgetsBelowVerticallyBy(movedWidget, movedWidget.h));
    this.handleWidgetsChange([movedWidget, ...additionalMovedWidgets], { checkForY: true });
  }

  public moveLeft(widgetId: string): void {
    if (!this.canMoveLeft(widgetId)) {
      throw new Error(`Cannot move widget ${widgetId} left`);
    }

    this.moveHorizontally(widgetId, 0);
  }

  public moveRight(widgetId: string): void {
    if (!this.canMoveRight(widgetId)) {
      throw new Error(`Cannot move widget ${widgetId} right`);
    }

    this.moveHorizontally(widgetId, 1);
  }

  private moveFullWidthWidgetUp(widget: UiDashboardWidget): UiDashboardWidget[] {
    const widgetsAbove = this.getWidgetsOnRowAbove(widget);
    const movedWidget = { ...widget, y: widgetsAbove[0].y };
    return [movedWidget, ...this.moveWidgetsVerticallyBy(widgetsAbove, widget.h)];
  }

  private moveFullWidthWidgetDown(widget: UiDashboardWidget): UiDashboardWidget[] {
    const widgetsBelow = this.getWidgetsOnRowBelow(widget);
    const movedWidget = { ...widget, y: widgetsBelow[0].y };
    return [movedWidget, ...this.moveWidgetsVerticallyBy(widgetsBelow, -widget.h)];
  }

  private getWidgetById(widgetId: string): UiDashboardWidget {
    const widget = this.widgets.find((w) => w.id === widgetId);
    if (!widget) {
      throw new Error(`Widget with id ${widgetId} not found`);
    }
    return widget;
  }

  private getWidgetsOnRowAbove(widget: UiDashboardWidget): UiDashboardWidget[] {
    const sortedWidgets = this.getSortedWidgetsVertically(this.widgets, { bottomToTop: true });
    const widgetAbove = sortedWidgets.find((w) => w.y < widget.y && w.id !== widget.id);
    const widgetsAbove: UiDashboardWidget[] = [];

    if (widgetAbove) {
      widgetsAbove.push(widgetAbove);

      const widgetOnSameRow = this.getWidgetOnSameRow(widgetAbove);
      if (widgetOnSameRow) {
        widgetsAbove.push(widgetOnSameRow);
      }
    }

    return widgetsAbove;
  }

  private getWidgetsOnRowBelow(widget: UiDashboardWidget): UiDashboardWidget[] {
    const sortedWidgets = this.getSortedWidgetsVertically(this.widgets);
    const widgetBelow = sortedWidgets.find((w) => w.y > widget.y && w.id !== widget.id);
    const widgetsBelow: UiDashboardWidget[] = [];

    if (widgetBelow) {
      widgetsBelow.push(widgetBelow);

      const widgetOnSameRow = this.getWidgetOnSameRow(widgetBelow);
      if (widgetOnSameRow) {
        widgetsBelow.push(widgetOnSameRow);
      }
    }

    return widgetsBelow;
  }

  private getWidgetsBelow(widget: UiDashboardWidget): UiDashboardWidget[] {
    return structuredClone(this.widgets.filter((w) => w.id !== widget.id && w.y > widget.y));
  }

  private moveWidgetsBelowVerticallyBy(widget: UiDashboardWidget, by: number): UiDashboardWidget[] {
    const widgetsBelow = this.getWidgetsBelow(widget);
    widgetsBelow.forEach((widgetBelow) => {
      widgetBelow.y += by;
    });
    return widgetsBelow;
  }

  private moveWidgetsVerticallyBy(widgets: UiDashboardWidget[], by: number): UiDashboardWidget[] {
    widgets.forEach((widget) => {
      widget.y += by;
    });
    return widgets;
  }

  private getWidgetOnSameRow(widget: UiDashboardWidget): UiDashboardWidget {
    return structuredClone(this.widgets.find((w) => w.y === widget.y && w.id !== widget.id));
  }

  private getSortedWidgetsVertically(widgets: UiDashboardWidget[], options: { bottomToTop: boolean } = { bottomToTop: false }): UiDashboardWidget[] {
    const sortedWidgets = structuredClone(widgets).sort((a, b) => {
      if (a.y < b.y) {
        return -1;
      } else if (a.y > b.y) {
        return 1;
      }
      return 0;
    });
    return options.bottomToTop ? sortedWidgets.reverse() : sortedWidgets;
  }

  private moveHorizontally(widgetId: string, x: number): void {
    const widget = this.getWidgetById(widgetId);

    const widgetOnSameRow = this.widgets.find((w) => w.y === widget.y && w.id !== widget.id);
    if (widgetOnSameRow) {
      const changedWidgets = this.swapWidgetsHorizontally(widget, widgetOnSameRow);
      this.handleWidgetsChange(changedWidgets);
    } else {
      this.handleWidgetsChange([
        {
          ...widget,
          x,
        },
      ]);
    }
  }

  private swapWidgetsHorizontally(widget1: UiDashboardWidget, widget2: UiDashboardWidget): UiDashboardWidget[] {
    const changedWidgets = [];
    changedWidgets.push({
      ...widget1,
      x: widget2.x,
      y: widget2.y,
    });
    changedWidgets.push({
      ...widget2,
      x: widget1.x,
      y: widget1.y,
    });

    return changedWidgets;
  }

  private swapWidgetsVertically(widget1: UiDashboardWidget, widget2: UiDashboardWidget): UiDashboardWidget[] {
    const sortedWidgets = this.getSortedWidgetsVertically([widget1, widget2]);

    sortedWidgets[1].y = sortedWidgets[0].y;
    sortedWidgets[0].y = sortedWidgets[1].y + sortedWidgets[1].h;

    return sortedWidgets;
  }

  private isCheckInsWidgetAvailable$(): Observable<boolean> {
    return zip([
      this.featureModuleService.isReflectionsModuleEnabled$(),
      this.permissionsFacade.hasPermission$("AccessPeople"),
      this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.FeatureReflections),
    ]).pipe(
      map(([isCheckInsModuleEnabled, hasAccessPeople, isCheckInsFeatureAvailable]) => {
        return isCheckInsModuleEnabled && hasAccessPeople && isCheckInsFeatureAvailable && !this.currentUserService.userHasRole("view-only");
      })
    );
  }

  private isWhiteboardsWidgetAvailable$(): Observable<boolean> {
    return this.featureModuleService.isWhiteboardModuleEnabled$();
  }

  private isTodosWidgetAvailable$(): Observable<boolean> {
    return zip([
      // required for okr approvals and kr updates
      this.permissionsFacade.hasPermission$("ManageGoals"),
      // required for check-in updates
      this.featureModuleService.isReflectionsModuleEnabled$(),
      this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.FeatureReflections),
      this.permissionsFacade.hasPermission$("AccessPeople"),
      // required for task updates
      this.featureModuleService.isTaskModuleEnabled$(),
      this.permissionsFacade.hasPermission$("ManageTasks"),
    ]).pipe(
      map(([canUpdateOkrs, checkInsModuleEnabled, checkInsFeatFlagEnabled, hasAccessPeaplePermission, isTasksModuleEnabled, hasManageTasksPermission]) => {
        const isInViewOnlyRole = this.currentUserService.userHasRole("view-only");
        const canUpdateCheckIns = checkInsModuleEnabled && checkInsFeatFlagEnabled && hasAccessPeaplePermission;
        const canUpdateTasks = isTasksModuleEnabled && hasManageTasksPermission;

        return !isInViewOnlyRole && (canUpdateOkrs || canUpdateCheckIns || canUpdateTasks);
      })
    );
  }

  private getCloseWidgetIdsFromUserSettings(): string[] {
    const settings = this.currentUserService.getUserSetting<HomeWidgetSettings>("homeWidgets");
    return settings?.closed || [];
  }

  private markWidgetAsClosedInUserSettings(widgetId: string): void {
    let settings = this.currentUserService.getUserSetting<HomeWidgetSettings>("homeWidgets");
    if (!settings) {
      settings = {};
    }
    if (!settings.closed) {
      settings.closed = [];
    }
    if (settings.closed.includes(widgetId)) {
      return;
    }

    settings.closed.push(widgetId);
    this.currentUserService.setUserSetting({ homeWidgets: settings });
  }

  private unmarkWidgetAsClosedInUserSettings(widgetId: string): void {
    const settings = this.currentUserService.getUserSetting<HomeWidgetSettings>("homeWidgets");
    if (!settings?.closed) {
      return;
    }

    const index = settings.closed.indexOf(widgetId);
    if (index < 0) {
      return;
    }

    settings.closed.splice(index, 1);
    this.currentUserService.setUserSetting({ homeWidgets: settings });
  }
}
